import { selectActiveElement } from "@/store/selections-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  CircularProgress,
  FontWeights,
  useDialog,
  useToast,
} from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import { Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useCreateAnnotations } from "./import-annotations-create";
import { getAnnotationsDescriptionFromCsvFile } from "./import-annotations-parse";
import { pickSingleFile } from "./pick-file";

/**
 * Hook to get a function to import annotations from a CSV file selected by the user.
 *
 * @returns An object containing functions to import annotations.
 */
export function useImportAnnotations(): (csvFile?: File) => Promise<void> {
  const { openToast } = useToast();
  const createAnnotations = useCreateAnnotations();
  const activeElement = useAppSelector(selectActiveElement);
  const [importProgress, setImportProgress] = useState<number | undefined>(
    undefined,
  );
  const { createDialog } = useDialog();

  const importAnnotations = useCallback(
    async (csvFile: File): Promise<void> => {
      assert(activeElement, "No active element");

      const resultImport = await getAnnotationsDescriptionFromCsvFile(csvFile);
      if (resultImport.errorMessage) {
        openToast({
          title: "Error importing annotations",
          variant: "error",
          message: resultImport.errorMessage,
        });
        return;
      }

      setImportProgress(0);
      const resultCreation = await createAnnotations(
        activeElement,
        resultImport.annotations,
        setImportProgress,
      );
      const title = `Imported ${resultCreation.numberAnnotationsCreated} of ${resultImport.annotations.length} annotations for element ${activeElement.name} `;
      const errorMessages = resultCreation.errorMessages.join("/n");
      if (resultCreation.numberAnnotationsCreated === 0) {
        openToast({
          title,
          variant: "error",
          message: errorMessages || "An unknown error occurred.",
          persist: true,
        });
      } else if (errorMessages) {
        openToast({
          title,
          variant: "warning",
          message: errorMessages,
          persist: true,
        });
      } else {
        openToast({
          title,
          variant: "success",
          message: `Successfully imported ${resultCreation.numberAnnotationsCreated} annotations.`,
        });
      }

      setImportProgress(undefined);
    },
    [openToast, activeElement, createAnnotations],
  );

  const dialogContents = useMemo(
    () => (
      <>
        {importProgress === undefined && (
          <Typography>
            Annotations from your CSV will be added to{" "}
            <span style={{ fontWeight: FontWeights.SemiBold }}>
              {activeElement?.name}
            </span>
            .
          </Typography>
        )}
        {importProgress !== undefined && (
          <Typography>Importing annotations...</Typography>
        )}
        {importProgress !== undefined && (
          <CircularProgress showPercentage value={importProgress} />
        )}
      </>
    ),
    [activeElement?.name, importProgress],
  );

  /**
   * Carries out the following steps:
   *  1) Checks that there is an active element.
   *  2) If no CSV file is provided, opens a file picker for selecting a CSV file.
   *  3) Shows a confirmation dialog.
   *  4) Starts the import of annotations from the selected CSV file.
   */
  const importAnnotationsWithDialog = useCallback(
    async (csvFileIn?: File): Promise<void> => {
      if (!activeElement) {
        openToast({
          title: "Error importing annotations",
          variant: "info",
          message: "No active element found.",
        });
        return;
      }

      const csvFile = csvFileIn ?? (await pickSingleFile(".csv"));
      if (!csvFile) {
        // user cancelled file selection
        openToast({
          title: "Error importing annotations",
          variant: "error",
          message: "No file was selected.",
        });
        return;
      }

      await createDialog({
        title: "Importing annotations",
        content: dialogContents,
        onConfirm: async () => {
          await importAnnotations(csvFile);
          return true;
        },
      });
    },
    [createDialog, activeElement, openToast, importAnnotations, dialogContents],
  );

  return importAnnotationsWithDialog;
}
